import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, filter, from, interval, mergeMap, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import _ from 'lodash';
import moment from 'moment';
import { AuxiliarService } from './auxiliar.service';
import { NGXLogger } from 'ngx-logger';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private httpClient: HttpClient,
    private cookieService: CookieService,
    private router: Router,
    private jwtService: JwtHelperService,
    private auxiliarService: AuxiliarService,
    private logger: NGXLogger,
  ) { }

  // [POST] log in
  public login(credentials: {username: string, password: string}): Observable<object> {
    return this.httpClient.post<HttpResponse<object>>
      (environment.api_server + environment.api_endpoints.login, credentials, {
        params: {}, withCredentials: false, observe: 'response'
      });
  }

  // log out
  public logout(): void {
    const urlTenant = this.cookieService.get('urlTenant') || 'benefit';
    // clear cookies
    const cookies = _.keys(this.cookieService.getAll());
    const rootCookiesToPreserve = ['urlTenant', 'logo-vertical', 'anonymous-id'];
    _.forEach(cookies, (c) => {
      if(!_.includes(rootCookiesToPreserve, c)) {
        this.cookieService.delete(c, '/');
        this.cookieService.delete(c, `/${urlTenant}`);
      }
    });
    // close drawers
    this.auxiliarService.closeDrawer('left');
    this.auxiliarService.closeDrawer('right');
    // redirect
    this.router.navigate([urlTenant, 'login']);
  }

  // get token field value
  public getTokenFieldValue(field: string): any {
    if(!field || !this.cookieService.get('auth')) return;
    return _.get(this.jwtService.decodeToken(this.cookieService.get('auth')), field);
  }

  // [PATCH] recover forgotten password
  public recoverPassword(username: string): Observable<object> {
    return this.httpClient.patch<HttpResponse<object>>
      (environment.api_server + environment.api_endpoints.passwordRecover, {username}, {
        params: {}, withCredentials: false, observe: 'response'
      });
  }

  // [PATCH] change password
  public changePassword(password: string): Observable<object> {
    return this.httpClient.patch<HttpResponse<object>>
      (environment.api_server + environment.api_endpoints.passwordChange, {password}, {
        params: {}, withCredentials: false, observe: 'response'
      });
  }

  // [PATCH] get new token
  public refreshToken(): Observable<object> {
    return this.httpClient.patch<HttpResponse<object>>
      (environment.api_server + environment.api_endpoints.refreshToken, {}, {
        params: {}, withCredentials: false, observe: 'response'
      });
  }

  // know if token expiration time in range to refresh it
  // if true, generate new token
  public checkTokenExp(): void {
    const exp = this.getTokenFieldValue('exp');
    const now = moment().unix();
    if(exp - now <= environment.tokenExpires) {
      this.refreshToken()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            this.cookieService.set('auth', _.get(res, 'body.data', ''), { 'path': '/' });
          }
        });
    }
  }

  // redirect according to tenant roles
  public redirectAccordingToRoles(roles: string[]): void {
    const urlTenant = this.cookieService.get('urlTenant') || 'benefit';
    if(this.getTokenFieldValue('isAdmin') || _.some(['auxiliar', 'frontdesk', 'manager', 'professional'], (role) => _.includes(roles, role))) {
      this.router.navigate([urlTenant, 'dashboard']);
    } else if (_.includes(roles, 'billing')) {
      this.router.navigate([urlTenant, 'billing']);
    } else if (_.includes(roles, 'employee')) {
      this.router.navigate([urlTenant, 'timesheets']);
    }
  }

  // [GET] anonymous features for app colors && images
  public getAnonymousFeatures(urlTenant: string): Observable<object> {
    return this.httpClient.get<HttpResponse<object>>
      (environment.api_server + _.replace(environment.api_endpoints.features, ':name', urlTenant), {
        params: {}, withCredentials: false, observe: 'response'
      });
  }

  // [GET] anonymous file
  public getAnonymousFile(file: string): Observable<HttpResponse<Blob>> {
    return this.httpClient.get(
      environment.api_server + environment.api_endpoints.file, {
        params: { uri: file }, withCredentials: false, observe: 'response', responseType: 'blob'
      }
    );
  }

  // function to get app logos
  public getLogo(name: string): Observable<string | undefined> {
    return interval(100).pipe(
      filter(() => this.cookieService.check(name)),
      take(1),
      switchMap(() => {
        const cookie = this.cookieService.get(name);
        return this.getAnonymousFile(cookie).pipe(
          mergeMap((res: any) => {
            const logoBlob = _.get(res, 'body');
            if (!logoBlob) return of(undefined);
            return from(this.auxiliarService.convertBlobToBase64(logoBlob));
          }),
          catchError(err => {
            this.logger.error('[authService] getAnonymousFile err:', err);
            return of(undefined);
          })
        );
      })
    );
  }
}
